import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface DomainScopeBarProps {
  clinicId?: string;
  clinicOptions?: Array<{ value: string; label: string }>;
  onClinicChange?: (value: string) => void;
  showClinic?: boolean;
  /** `all` | id de consultorio */
  sedeId?: string;
  sedeOptions?: Array<{ value: string; label: string }>;
  onSedeChange?: (value: string) => void;
  showSede?: boolean;
  actions?: ReactNode;
}

/** Filtros de clínica/sucursal (el periodo vive en el chip del shell). */
export function DomainScopeBar({
  clinicId,
  clinicOptions = [],
  onClinicChange,
  showClinic = false,
  sedeId = 'all',
  sedeOptions = [],
  onSedeChange,
  showSede = false,
  actions,
}: DomainScopeBarProps) {
  const { t } = useTranslation();
  const hasFilters = showClinic || showSede || Boolean(actions);
  if (!hasFilters) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap items-center gap-3">
        {showClinic && onClinicChange && (
          <label className="inline-flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
            <span className="whitespace-nowrap text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {t('domain.clinicFilter')}
            </span>
            <select
              value={clinicId ?? ''}
              onChange={(event) => onClinicChange(event.target.value)}
              className="max-w-[16rem] rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-800 shadow-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-slate-100"
            >
              {clinicOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        )}
        {showSede && onSedeChange && sedeOptions.length > 0 && (
          <label className="inline-flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
            <span className="whitespace-nowrap text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {t('domain.branchFilter')}
            </span>
            <select
              value={sedeId}
              onChange={(event) => onSedeChange(event.target.value)}
              data-testid="domain-scope-sede"
              className="max-w-[16rem] rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-800 shadow-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-slate-100"
            >
              <option value="all">{t('domain.allBranches')}</option>
              {sedeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}
